import * as React from 'react'

import NextImage from '@/components/NextImage'
import { Badge } from '@/components/ui/badge'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'

import { CompanyType } from '@/types/hiring.type'

export default function CompanyChip({ company }: { company: CompanyType }) {
  const normalizedType = company.type ? company.type.replace(/_/g, ' ').toLowerCase() : null
  const domainHref = company.domain
    ? company.domain.startsWith('http')
      ? company.domain
      : `https://${company.domain}`
    : null

  return (
    <HoverCard openDelay={120} closeDelay={100}>
      <HoverCardTrigger asChild>
        <span>
          <Badge
            variant="secondary"
            className="cursor-pointer max-w-[140px] sm:max-w-[200px] truncate bg-muted/70 text-foreground hover:bg-muted border-blue-400 text-blue-700"
            title={company.name}
          >
            {company.name}
          </Badge>
        </span>
      </HoverCardTrigger>
      <HoverCardContent className="w-80">
        <div className="flex items-start gap-3">
          {company.logo ? (
            <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden border">
              <NextImage
                alt={company.name}
                height={40}
                width={40}
                src={company.logo}
                classNames={{ image: 'object-cover w-full h-full' }}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-blue-100 to-blue-300 dark:from-blue-900 dark:to-blue-700 text-blue-700 dark:text-blue-200 text-sm font-bold border">
              {company.name
                .split(' ')
                .map(word => word[0])
                .join('')
                .slice(0, 2)
                .toUpperCase()}
            </div>
          )}
          <div className="flex-1 space-y-1">
            <p className="text-sm font-medium leading-none">{company.name}</p>
            <div className="flex flex-wrap items-center gap-2">
              {normalizedType && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground uppercase">
                  {normalizedType}
                </span>
              )}
              {company.size && <span className="text-[10px] text-muted-foreground">Size: {company.size}</span>}
              {domainHref && (
                <a
                  href={domainHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300"
                >
                  {domainHref.replace(/^https?:\/\//, '')}
                </a>
              )}
            </div>
          </div>
        </div>
        {company.description && (
          <p className="mt-2 text-xs text-muted-foreground line-clamp-4">{company.description}</p>
        )}
      </HoverCardContent>
    </HoverCard>
  )
}
